// Müşteri profili › "Raporlama takvimi" kartı (M17 T5, migration 0058): mali yıl başlangıç ayı.
// 1 = takvim yılı (varsayılan, tüm yüzeyler bugünküyle aynı); ≠1 → KPI yıl sütunları "MY2026".
// Form soruları ve proje yılı değişmez. Müşteride KPI değeri varken ay değiştirilemez (sunucu 409;
// kart "Düzenle"yi göstermez + kırmızı uyarı). Düzenleme izni clients.edit_all; kalıp
// ClientEvidencePolicyCard (kart, düzenle/kaydet/vazgeç, kaydedildi bildirimi, yükleme hatası).

import { useCallback, useEffect, useState } from "react";
import { MONTH_NAMES_TR, fiscalPeriodRange, shortDateRange, type Client } from "@mahir/shared";
import { api, ApiError } from "../../api/client";
import { CLIENT_CALENDAR_CARD_COPY as C } from "../../lib/client-admin-copy";
import { yearHeaderLabel } from "../../lib/kpi-ui";
import { useAuthStore } from "../../stores/auth-store";

interface Props {
  clientId: string;
}

/** Görünen değer: "Takvim yılı (Ocak)" ya da "Temmuz — MY2026: 1 Tem 2025 – 30 Haz 2026". */
export function describeFiscalStart(startMonth: number, year: number): string {
  if (startMonth === 1) return C.calendarYear;
  const r = fiscalPeriodRange(year, startMonth, "annual");
  return C.fiscalValue(
    MONTH_NAMES_TR[startMonth - 1] ?? "",
    yearHeaderLabel(year, startMonth),
    shortDateRange(r.start, r.end),
  );
}

export function ClientReportingCalendarCard({ clientId }: Props): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const canEdit = me?.permissions["clients.edit_all"] === true;
  const [client, setClient] = useState<Client | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback((): (() => void) => {
    let aborted = false;
    setLoadFailed(false);
    api<{ client: Client }>(`/clients/${clientId}`)
      .then((res) => {
        if (aborted) return;
        setClient(res.client);
        setDraft(res.client.fiscalYearStartMonth);
      })
      .catch(() => {
        if (!aborted) setLoadFailed(true);
      });
    return () => {
      aborted = true;
    };
  }, [clientId]);

  useEffect(() => load(), [load]);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ client: Client }>(`/clients/${clientId}`, {
        method: "PATCH",
        body: { fiscalYearStartMonth: draft },
      });
      setClient(res.client);
      setDraft(res.client.fiscalYearStartMonth);
      setEditing(false);
      setNotice(C.saved);
    } catch (err) {
      // 409 yedeği: kart açıkken başka yoldan değer yazıldıysa kilit metni
      setError(
        err instanceof ApiError && err.status === 409
          ? C.locked
          : err instanceof Error
            ? err.message
            : C.saveError,
      );
    } finally {
      setBusy(false);
    }
  }

  const locked = (client?.kpiValueCount ?? 0) > 0;
  const labelStyle = { color: "var(--color-text-3)", fontSize: "0.72rem" } as const;
  const valueStyle = { color: "var(--color-text)", fontSize: "0.85rem" } as const;
  const inputStyle = {
    width: "100%",
    minHeight: 32,
    padding: "4px 8px",
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    fontSize: "0.85rem",
  } as const;
  const selectId = `fiscal-start-${clientId}`;

  return (
    <div
      data-testid="client-reporting-calendar-card"
      className="rounded-xl p-5"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div className="flex items-center justify-between mb-2">
        <h3
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.95rem",
            color: "var(--color-text)",
          }}
        >
          {C.title}
        </h3>
        {canEdit && !editing && client && !locked ? (
          <button
            type="button"
            onClick={() => {
              setNotice(null);
              setError(null);
              setEditing(true);
            }}
            className="text-xs"
            style={{ color: "var(--color-primary)", fontWeight: 600 }}
          >
            {C.edit}
          </button>
        ) : null}
      </div>

      {loadFailed ? (
        <div role="alert" className="text-xs" style={{ color: "var(--color-error)" }}>
          {C.loadError}{" "}
          <button
            type="button"
            onClick={() => load()}
            className="underline"
            style={{ color: "var(--color-primary)" }}
          >
            {C.retry}
          </button>
        </div>
      ) : !client ? (
        <p style={labelStyle}>{C.loading}</p>
      ) : editing ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <p style={labelStyle}>{C.hint}</p>
          <div className="flex flex-col gap-1">
            <label htmlFor={selectId} style={labelStyle}>
              {C.fiscalStart}
            </label>
            <select
              id={selectId}
              value={draft}
              onChange={(e) => setDraft(Number(e.target.value))}
              style={inputStyle}
            >
              {MONTH_NAMES_TR.map((m, i) => (
                <option key={m} value={i + 1}>
                  {C.monthOption(m, i)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy}
              className="px-3 py-1.5 rounded text-sm"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              {C.save}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setDraft(client.fiscalYearStartMonth);
                setEditing(false);
                setError(null);
              }}
              className="px-3 py-1.5 rounded text-sm"
              style={{ border: "1px solid var(--color-border)", color: "var(--color-text-2)" }}
            >
              {C.cancel}
            </button>
          </div>
        </form>
      ) : (
        <dl className="flex flex-col gap-2">
          <div>
            <dt style={labelStyle}>{C.fiscalStart}</dt>
            <dd style={valueStyle}>
              {describeFiscalStart(client.fiscalYearStartMonth, new Date().getFullYear())}
            </dd>
          </div>
          <p style={labelStyle}>{C.hint}</p>
        </dl>
      )}

      {/* D10: kilit engelleyicidir → kırmızı (amber yalnız bloklamayan uyarı) */}
      {canEdit && locked && !editing ? (
        <div role="alert" className="text-xs mt-2" style={{ color: "var(--color-error)" }}>
          {C.locked}
        </div>
      ) : null}
      {error ? (
        <div role="alert" className="text-xs mt-2" style={{ color: "var(--color-error)" }}>
          {error}
        </div>
      ) : null}
      {notice ? (
        <div role="status" className="text-xs mt-2" style={{ color: "var(--color-success)" }}>
          ✓ {notice}
        </div>
      ) : null}
    </div>
  );
}
